import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { apiRequest } from '@/lib/queryClient';
import { useAuth } from './use-auth';

// Define tenant types
export type Tenant = {
  id: number;
  name: string;
  domain: string;
  plan: string;
  status: string;
  settings?: string;
  customization?: string;
};

type TenantContextType = {
  tenant: Tenant | null;
  isLoading: boolean;
  error: Error | null;
  updateTenant: (data: Partial<Tenant>) => Promise<Tenant | null>;
};

// Create the context
export const TenantContext = createContext<TenantContextType | null>(null);

// Provider component
export function TenantProvider({ children }: { children: ReactNode }) {
  const [tenant, setTenant] = useState<Tenant | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);
  const auth = useAuth();

  // Fetch the tenant data when the authenticated user changes
  useEffect(() => {
    const fetchTenant = async () => {
      if (!auth?.user) {
        setIsLoading(false);
        return;
      }

      // Get tenant ID from authenticated user
      const tenantId = auth.user.tenantId;
      
      if (!tenantId) {
        // Super admins don't have tenant IDs, this is expected behavior
        if (!auth.user.isSuperAdmin) {
          console.error('No tenant ID in user object:', auth.user);
        }
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        
        // Using the current user's tenant data from auth context
        // This creates a basic tenant object without needing a separate API call
        const basicTenantData = {
          id: tenantId,
          name: 'Current Organization',
          domain: '',
          plan: 'current',
          status: 'active'
        };
        
        setTenant(basicTenantData);
        setError(null);
        
        // Attempt to fetch full tenant data in the background
        // If this fails, we'll still have basic data to work with
        try {
          const tenantData = await apiRequest<Response>(`/api/tenants/${tenantId}`, {
            method: 'GET'
          });
          
          if (tenantData.ok) {
            const fullTenant = await tenantData.json();
            setTenant(fullTenant);
          }
        } catch (bgError) {
          console.warn('Background tenant fetch failed:', bgError);
          // We keep the basic tenant data and don't set an error
        }
      } catch (error) {
        console.error('Error setting up tenant data:', error);
        setError(error instanceof Error ? error : new Error('Failed to set up tenant data'));
        setTenant(null);
      } finally {
        setIsLoading(false);
      }
    };

    fetchTenant();
  }, [auth?.user]);

  // Function to update tenant data
  const updateTenant = async (data: Partial<Tenant>): Promise<Tenant | null> => {
    if (!tenant) return null;

    try {
      setIsLoading(true);
      const response = await apiRequest<Response>(`/api/tenants/${tenant.id}`, {
        method: 'PUT',
        data
      });
      const updatedTenant = await response.json();
      setTenant(updatedTenant);
      return updatedTenant;
    } catch (error) {
      console.error('Error updating tenant:', error);
      setError(error instanceof Error ? error : new Error('Failed to update tenant data'));
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  const contextValue: TenantContextType = {
    tenant,
    isLoading,
    error,
    updateTenant
  };

  return (
    <TenantContext.Provider value={contextValue}>
      {children}
    </TenantContext.Provider>
  );
}

// Hook to use the tenant context
export function useTenant() {
  const context = useContext(TenantContext);
  
  if (!context) {
    throw new Error('useTenant must be used within a TenantProvider');
  }
  
  return context;
}